import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Users, FileText, CheckCircle2, Activity, Loader2, FileDown, Scale } from "lucide-react";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const downloadDossier = async () => {
    try {
      setDownloading(true);
      const res = await api.get("/admin/compliance-dossier", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      }));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.download = `GoStreet_Dossier_Legale_${today}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Errore generazione dossier: " + (e?.response?.data?.detail || e.message));
    } finally {
      setDownloading(false);
    }
  };

  if (!stats) return <DashboardLayout role="superadmin"><div className="p-8"><Loader2 className="w-8 h-8 animate-spin text-[#2456C6]" /></div></DashboardLayout>;

  const cards = [
    { label: "Consulenti attivi", value: stats.consultants.active, sub: `su ${stats.consultants.total} totali`, icon: Users, color: "#2456C6" },
    { label: "Pratiche totali", value: stats.practices.total, sub: "tutti gli stati", icon: FileText, color: "#F77F2A" },
    { label: "Pratiche aperte", value: stats.practices.open, sub: "in lavorazione", icon: Activity, color: "#EAB308" },
    { label: "Pratiche chiuse", value: stats.practices.closed, sub: "archiviate", icon: CheckCircle2, color: "#22C55E" },
  ];

  return (
    <DashboardLayout role="superadmin">
      <div className="p-4 sm:p-8">
        <h1 className="text-2xl sm:text-3xl font-black text-[#1E293B] mb-1">Panoramica</h1>
        <p className="text-sm text-slate-500 mb-6">Statistiche globali piattaforma GoStreet</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {cards.map((c, i) => (
            <div key={i} className="card-gs">
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: `${c.color}15`, color: c.color }}>
                  <c.icon className="w-5 h-5" />
                </div>
              </div>
              <div className="text-3xl font-black text-[#1E293B]" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, '-')}`}>{c.value}</div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mt-1">{c.label}</div>
              <div className="text-xs text-slate-400 mt-1">{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="card-gs">
          <h2 className="font-bold text-[#1E293B] mb-4">Riepilogo clienti</h2>
          <div className="text-3xl font-black text-[#2456C6]">{stats.clients}</div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mt-1">Automobilisti registrati</div>
        </div>

        {/* Compliance / Legal dossier */}
        <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50/40 p-5 sm:p-6" data-testid="admin-compliance-card">
          <div className="flex items-start gap-4 flex-col sm:flex-row sm:items-center justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                <Scale className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-black text-[#1E293B] text-base">Dossier Legale & Compliance</h3>
                <p className="text-sm text-slate-600 mt-1 max-w-2xl">
                  Documento Word completo con stato attuale (Privacy, T&amp;C, Cookie, AI, sub-processor, flussi dati) e <b>bozze contrattuali pronte</b> con clausole suggerite (T&amp;C B2B, DPA art. 28 GDPR, Privacy doppio Titolare/Responsabile, consenso art. 9 GDPR, rinnovo automatico, free trial, AI).
                  Consegnalo al tuo legale per la revisione: lui ti rimanderà il file con correzioni in rosso.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={downloadDossier}
              disabled={downloading}
              data-testid="download-compliance-dossier-btn"
              className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#1E293B] hover:bg-[#0F172A] text-white font-bold text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {downloading ? "Genero…" : "Scarica .docx"}
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
